import { useSignal } from '@preact/signals'
import { HTMLWidget } from 'apps/admin/widgets.ts'
import type { Product } from 'apps/commerce/types.ts'
import Checkbox from 'site/components/ui/Checkbox.tsx'
import RenderHTML from 'site/components/ui/RenderHTML.tsx'
import { Runtime } from 'site/runtime.ts'
import c from 'site/sdk/c.ts'
import v, { is } from 'site/sdk/v.ts'
import type { JSX } from 'preact'

export type OutOfStockTexts = {
    /**
     * @title Título
     */
    title: string
    /**
     * @title Descrição
     */
    description: string

    /**
     * @title Label de email
     */
    emailLabel: string
    /**
     * @title Placeholder de email
     */
    emailPlaceholder: string

    /**
     * @title Label de nome
     */
    nameLabel: string
    /**
     * @title Placeholder de nome
     */
    namePlaceholder: string

    /**
     * @title Texto do botão para o usuário aceitar o recebimento de emails
     */
    acceptText: string
    /**
     * @title Texto do botão de enviar
     */
    submitText: string

    /**
     * @title Erro quando a pessoa deixa o campo de nome vazio
     */
    emptyNameError: string

    /**
     * @title Erro quando a pessoa deixa o campo de email vazio
     */
    emptyEmailError: string

    /**
     * @title Erro quando a pessoa usa um formato de email inválido
     */
    invalidEmailError: string

    /**
     * @title Texto exibido quando o usuário aceitar o recebimento de emails
     */
    successText: HTMLWidget

    /**
     * @title Botão de voltar
     */
    backText: string

    /**
      * @title Modulo Reduzido
      */
    modoReduzido?: 'Desligado' | 'Ligado'
    /**
    * @title Título Reduzido
    */
    titleReduzido: string
    /**
     * @title Descrição Reduzido
     */
    descriptionReduzido: string
    /**
     * @title Label Link Reduzido
     */
    labelLinkReduzido: string
}

export interface Props {
  productID: Product['productID']
  texts: OutOfStockTexts
  categoryLink: string
}

function Notify({ productID, texts, categoryLink }: Props) {
    const loading = useSignal(false)
    const errName = useSignal('')
    const errEmail = useSignal('')
    const sended = useSignal(false)

    const vvName = v<string>((i) => !i && texts.emptyNameError)

    const vvEmail = v<string>(
        (i) => !i && texts.emptyEmailError,
        (i) => !is.email(i) && texts.invalidEmailError,
    )

    const handleSubmit: JSX.GenericEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault()

        try {
            loading.value = true

            const name = (e.currentTarget.elements.namedItem('name') as RadioNodeList)?.value
            const email = (e.currentTarget.elements.namedItem('email') as RadioNodeList)?.value
            const accept = (e.currentTarget.elements.namedItem('accept') as HTMLInputElement)
                ?.checked

            errName.value = vvName(name) ?? ''
            errEmail.value = vvEmail(email) ?? ''

            if (errName.value || errEmail.value || !accept) return

            await Runtime.vtex.actions.notifyme({ skuId: productID, name, email })

            sended.value = true
        } finally {
            loading.value = false
        }
    }

    if (sended.value) {
        return (
            <>
                <RenderHTML
                    html={texts.successText}
                    class={c(
                        'mt-16 flex flex-col gap-y-2',
                        '[&_strong]:text-2xl [&_strong]:text-successs [&_strong]:font-extrabold',
                        '[&_p]:text-sm [&_p]:text-neutral-2',
                    )}
                />
                <button
                    type='button'
                    class='btn disabled:loading bg-neutral-1 hover:bg-neutral-0 transition-colors mt-6 text-white'
                    onClick={() => {
                        sended.value = false
                    }}
                >
                    {texts.backText}
                </button>
            </>
        )
    }

    return texts.modoReduzido === 'Ligado' ? (
      <div>
        <div class='justify-start gap-y-2'>
          <div class='text-neutral-1 text-lg mb-2 font-extrabold'>{texts.titleReduzido}</div>
          <div class='text-neutral-2 text-sm'>{texts.descriptionReduzido}</div>
          <a
            href={categoryLink}
            class="btn w-full text-base text-white bg-primary-700 hover:bg-primary-500 hover:text-white transition-colors mt-6"
          >
            {texts.labelLinkReduzido}
          </a>
        </div>
      </div>
    ) : (
      <form class='form-control justify-start gap-y-2' onSubmit={handleSubmit} noValidate>
        <span class='text-neutral-1 text-2xl font-extrabold'>{texts.title}</span>
        <span class='text-neutral-2 text-sm'>{texts.description}</span>

        <label class='flex flex-col gap-y-2 mt-6'>
          <span class='text-sm text-neutral-1 font-bold block ml-2.5'>{texts.nameLabel}</span>
          <input
            placeholder={texts.namePlaceholder}
            class={c(
              'input input-bordered !outline-none',
              errName.value && 'border border-danger-700',
            )}
            name='name'
            type='text'
            onInput={() => {
              errName.value = ''
            }}
          />
          {errName.value && <span class='text-xs text-danger-700'>{errName.value}</span>}
        </label>
        <label class='flex flex-col gap-y-2 mt-4'>
          <span class='text-sm text-neutral-1 font-bold block ml-2.5'>
            {texts.emailLabel}
          </span>
          <input
            placeholder={texts.emailPlaceholder}
            class={c(
              'input input-bordered !outline-none',
              errName.value && 'border border-danger-700',
            )}
            name='email'
            type='email'
            onInput={() => {
              errEmail.value = ''
            }}
          />
          {errEmail.value && <span class='text-xs text-danger-700'>{errEmail.value}</span>}
        </label>

        <label class='flex items-start gap-x-2 cursor-pointer'>
          <Checkbox />
          <span class='font-semibold text-sm max-w-[320px] select-none'>
            {texts.acceptText}
          </span>
        </label>

        <button
          type='submit'
          class='btn disabled:loading bg-neutral-1 hover:bg-neutral-0 transition-colors mt-6 text-white'
          disabled={loading}
        >
          {texts.submitText}
        </button>
      </form>
    )
}

export default Notify
